import { Divider, Form, message, Spin } from 'antd';
import { Banner } from 'components/ui/Banner';
import { useCallback, useEffect, useState } from 'react';
import { FAMILY_APPLICATION_STEPS } from '../../familyApplicationConstants';
import PageMeta from 'components/PageMeta/PageMeta';
import RadioCards from 'components/RadioCards/RadioCards';
import AddressFields from 'components/AddressFields';
import { familyService } from 'services';
import { getNextStep, getPreviousStep, stepConfigAtom } from '../../atoms';
import { useAtomValue } from 'jotai';
import { getApplicantFirstName, getOtherAdultFirstName } from '../../utils';
import { ApplicationWizardActions } from 'components/application-wizards/ApplicationWizardActions';

export function FamilyAppSpouseResidenceStatus({
  application,
  setApplication,
  household,
  loading,
  setStep,
  readOnly = false,
}) {
  const stepConfig = useAtomValue(stepConfigAtom);

  const [submitting, setSubmitting] = useState(false);

  // Application
  const isCompletedBySponsor = application?.completedBySponsor === true;

  // Form state
  const [form] = Form.useForm();
  const addressSameAsApplicantFormValue = Form.useWatch('addressSameAsApplicant', form);

  const onSubmit = useCallback(
    async (values) => {
      const _goToNextStep = (_newApplication) => {
        // Use getNextStep to determine the next step
        const nextStep = getNextStep(FAMILY_APPLICATION_STEPS.SPOUSE_RESIDENCE_STATUS, _newApplication, stepConfig);
        setStep({
          applicationId: _newApplication?.id,
          newStep: nextStep,
        });
      };

      if (readOnly) {
        _goToNextStep(application);
        return;
      }

      try {
        setSubmitting(true);

        const updatedSpouse = await familyService.addUpdateAdult(
          household?.id,
          application?.additionalParents?.[0]?.id ?? null,
          {
            ...application?.additionalParents?.[0],
            addressSameAsApplicant: values?.addressSameAsApplicant,
            homeAddress: values?.addressSameAsApplicant ? null : values?.homeAddress,
          },
        );

        let _newApplication = await familyService.saveApplication({
          ...application,
          additionalParents: [updatedSpouse],
        });

        // FAMILY-APPS__DATA_CLEANING - Clear children's living situation when spouse address changes
        const spouseAddressChanged = application?.additionalParents?.[0]?.homeAddress !== updatedSpouse.homeAddress;
        if (spouseAddressChanged && _newApplication?.children?.length > 0) {
          const updatedChildren = _newApplication.children.map((child) => ({
            ...child,
            livingSituation: null,
          }));

          // Update each child
          const childUpdatePromises = updatedChildren.map((child) =>
            familyService.addUpdateFamilyChild(household?.id, child.id, child),
          );
          const savedChildren = await Promise.all(childUpdatePromises);

          _newApplication = await familyService.saveApplication({
            ..._newApplication,
            children: savedChildren,
          });
        }

        setApplication(_newApplication);
        _goToNextStep(_newApplication);
      } catch (error) {
        message.error('Something went wrong');
        newrelic.noticeError(error);
      } finally {
        setSubmitting(false);
      }
    },
    [application, household?.id, readOnly, setApplication, setStep, stepConfig],
  );

  // Set initial form values
  useEffect(() => {
    if (application?.additionalParents?.[0]) {
      form.setFieldsValue({
        addressSameAsApplicant: application?.additionalParents?.[0]?.addressSameAsApplicant,
        homeAddress: application?.additionalParents?.[0]?.homeAddress,
      });
    }
  }, [application, form]);

  if (loading) {
    return (
      <div className="p-8">
        <Spin className="table m-auto" />
      </div>
    );
  }

  return (
    <Spin spinning={false}>
      <PageMeta title="Residence Status" />

      <Form form={form} layout="vertical" onFinish={onSubmit} disabled={readOnly}>
        <div className="p-7 pt-0">
          <Banner
            question={
              isCompletedBySponsor
                ? `Does ${getOtherAdultFirstName(application?.additionalParents?.[0])} live with you?`
                : `Do you live with ${getApplicantFirstName(application?.applicant)}?`
            }
            className="my-12"
          />

          <Form.Item
            name="addressSameAsApplicant"
            rules={[
              {
                required: true,
                message: 'Please specify if you live at the same address',
              },
            ]}
          >
            <RadioCards
              options={[
                {
                  label: 'Yes, same address',
                  value: true,
                },
                {
                  label: 'No, different address',
                  value: false,
                },
              ]}
            />
          </Form.Item>

          {addressSameAsApplicantFormValue === false && (
            <>
              <Divider dashed className="my-12" />

              <Banner
                variant="inline"
                question={isCompletedBySponsor ? 'Where do they live?' : 'Where do you live?'}
                className="mt-12 mb-8"
              />

              <AddressFields addressKey="homeAddress" />
            </>
          )}
        </div>

        <ApplicationWizardActions
          applicationIsReadOnly={readOnly}
          backButtonProps={{
            disabled: submitting,
            onClick: () => {
              // Use getPreviousStep to determine the previous step
              const previousStep = getPreviousStep(
                FAMILY_APPLICATION_STEPS.SPOUSE_RESIDENCE_STATUS,
                application,
                stepConfig,
              );

              setStep({
                applicationId: application?.id,
                newStep: previousStep,
              });
            },
          }}
          nextButtonProps={{
            loading: submitting,
          }}
        />
      </Form>
    </Spin>
  );
}
